// ═══════════════════════════════════════════════════════════
// Public Upload Page — /upload/:token
//
// No login required. Staff share this URL with department
// colleagues who need to provide documents for a FOIA request.
// The token identifies the request and carries instructions.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { IconUpload, IconFileText, IconCheck, IconAlertCircle, IconLoader2, IconX } from '@tabler/icons-react';
import { validateToken, type UploadToken } from '../../api/uploadTokens';
import { fetchRecord } from '../../api/client';
import { uploadAndCreateDocument } from '../../api/foiaDocuments';
import { TABLES } from '../../api/tables';
import { FIELDS } from '../../api/fields';

type PageState = 'loading' | 'invalid' | 'expired' | 'form' | 'uploading' | 'done';

interface FileItem {
  file: File;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

function formatBytes(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default function UploadPage() {
  const { token } = useParams<{ token: string }>();

  const [pageState, setPageState]         = useState<PageState>('loading');
  const [uploadToken, setUploadToken]     = useState<UploadToken | null>(null);
  const [requestSubject, setRequestSubject] = useState('');
  const [name, setName]                   = useState('');
  const [email, setEmail]                 = useState('');
  const [files, setFiles]                 = useState<FileItem[]>([]);
  const [dragOver, setDragOver]           = useState(false);
  const [nameError, setNameError]         = useState('');
  const [filesError, setFilesError]       = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) { setPageState('invalid'); return; }

    validateToken(token).then(async t => {
      if (!t) { setPageState('invalid'); return; }
      setUploadToken(t);

      // Fetch just the subject from the request record
      try {
        const row = await fetchRecord<Record<string, unknown>>(TABLES.requests, t.requestId, { anonymous: true });
        if (row) setRequestSubject(String(row[FIELDS.requests.subject] ?? ''));
      } catch { /* subject is best-effort */ }

      setPageState('form');
    });
  }, [token]);

  function addFiles(incoming: FileList | File[]) {
    const arr = Array.from(incoming);
    setFiles(prev => {
      const existing = new Set(prev.map(f => f.file.name + f.file.size));
      const deduped = arr.filter(f => !existing.has(f.name + f.size));
      return [...prev, ...deduped.map(f => ({ file: f, status: 'pending' as const }))];
    });
    setFilesError('');
  }

  function removeFile(idx: number) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    let valid = true;

    if (!name.trim()) { setNameError('Please enter your name.'); valid = false; }
    else setNameError('');

    if (files.length === 0) { setFilesError('Please add at least one file.'); valid = false; }
    else setFilesError('');

    if (!valid || !uploadToken) return;

    setPageState('uploading');

    for (let i = 0; i < files.length; i++) {
      setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'uploading' } : f));
      try {
        await uploadAndCreateDocument({
          requestId:    uploadToken.requestId,
          file:         files[i].file,
          source:       'upload-link',
          uploadedByEmail: email.trim() || '',
          uploaderName: name.trim() + (email.trim() ? ` <${email.trim()}>` : ''),
          notes:        `Uploaded via upload link. Uploader: ${name.trim()}${email.trim() ? ` (${email.trim()})` : ''}`,
          anonymous:    true,
        });
        setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'done' } : f));
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed.';
        setFiles(prev => prev.map((f, idx) => idx === i ? { ...f, status: 'error', error: msg } : f));
      }
    }

    setPageState('done');
  }

  // ── Shell ────────────────────────────────────────────────

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        background: 'white', borderBottom: '1px solid var(--color-neutral-200)',
        padding: '0 32px', height: 52, display: 'flex', alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src="/opengov-mark.svg" alt="OpenGov" style={{ height: 20, width: 'auto' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-neutral-900)', letterSpacing: '-0.01em', lineHeight: 1 }}>OpenGov</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-primary-700)', letterSpacing: '0.01em', lineHeight: 1 }}>OpenRecords</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 24px' }}>
        <div style={{ width: '100%', maxWidth: 560 }}>

          {/* Loading */}
          {pageState === 'loading' && (
            <div style={{ textAlign: 'center', color: 'var(--color-neutral-400)', paddingTop: 80 }}>
              <IconLoader2 size={28} style={{ animation: 'spin 1s linear infinite' }} />
              <div style={{ marginTop: 12, fontSize: 14 }}>Verifying link…</div>
            </div>
          )}

          {/* Invalid / expired */}
          {(pageState === 'invalid' || pageState === 'expired') && (
            <div style={{ background: 'white', border: '1px solid var(--color-neutral-200)', borderRadius: 12, padding: '40px 32px', textAlign: 'center' }}>
              <div style={{
                width: 48, height: 48, borderRadius: 99, background: 'var(--cds-red-50)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
              }}>
                <IconAlertCircle size={22} style={{ color: 'var(--cds-red-700)' }} />
              </div>
              <h1 style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 8px' }}>
                {pageState === 'expired' ? 'This link has expired' : 'Invalid upload link'}
              </h1>
              <p style={{ fontSize: 14, color: 'var(--color-neutral-500)', margin: 0, lineHeight: 1.6 }}>
                {pageState === 'expired'
                  ? 'This upload link is no longer active. Please contact the records officer who sent you this link to request a new one.'
                  : 'This upload link is invalid or has been revoked. Please contact the person who sent you this link.'}
              </p>
            </div>
          )}

          {/* Upload form */}
          {(pageState === 'form' || pageState === 'uploading') && (
            <form onSubmit={handleSubmit}>
              {/* Context card */}
              <div style={{ background: 'white', border: '1px solid var(--color-neutral-200)', borderRadius: 12, padding: '24px 28px', marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--color-neutral-500)', marginBottom: 6 }}>
                  Document Request
                </div>
                {requestSubject && (
                  <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-neutral-900)', margin: '0 0 12px', lineHeight: 1.4 }}>
                    {requestSubject}
                  </h2>
                )}
                {uploadToken?.label && (
                  <p style={{ fontSize: 14, color: 'var(--color-neutral-600)', margin: 0, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    {uploadToken.label}
                  </p>
                )}
              </div>

              {/* Your info */}
              <div style={{ background: 'white', border: '1px solid var(--color-neutral-200)', borderRadius: 12, padding: '24px 28px', marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-neutral-700)', marginBottom: 16 }}>Your Information</div>

                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-600)', marginBottom: 5 }}>
                    Full Name <span style={{ color: 'var(--cds-red-700)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Jane Smith"
                    value={name}
                    onChange={e => { setName(e.target.value); setNameError(''); }}
                    disabled={pageState === 'uploading'}
                    autoFocus
                  />
                  {nameError && <div style={{ fontSize: 11, color: 'var(--cds-red-700)', marginTop: 4 }}>{nameError}</div>}
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-600)', marginBottom: 5 }}>
                    Email <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-neutral-400)' }}>(optional)</span>
                  </label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="jane@example.gov"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    disabled={pageState === 'uploading'}
                  />
                </div>
              </div>

              {/* File upload */}
              <div style={{ background: 'white', border: '1px solid var(--color-neutral-200)', borderRadius: 12, padding: '24px 28px', marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-neutral-700)', marginBottom: 16 }}>Files</div>

                {/* Drop zone */}
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: `2px dashed ${dragOver ? 'var(--color-primary-500)' : filesError ? '#FCA5A5' : 'var(--color-neutral-200)'}`,
                    borderRadius: 8, padding: '28px 20px', textAlign: 'center',
                    cursor: pageState === 'uploading' ? 'default' : 'pointer',
                    background: dragOver ? 'var(--cds-blurple-50)' : 'transparent',
                    transition: 'all 0.15s',
                    pointerEvents: pageState === 'uploading' ? 'none' : 'auto',
                  }}
                >
                  <IconUpload size={22} style={{ color: 'var(--color-neutral-300)', marginBottom: 8 }} />
                  <div style={{ fontSize: 13, color: 'var(--color-neutral-600)' }}>
                    Drop files here or <span style={{ color: 'var(--color-primary-700)', fontWeight: 500 }}>click to browse</span>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--color-neutral-400)', marginTop: 4 }}>
                    Any file type — PDF, Word, images, spreadsheets
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
                />
                {filesError && <div style={{ fontSize: 11, color: 'var(--cds-red-700)', marginTop: 6 }}>{filesError}</div>}

                {/* File list */}
                {files.length > 0 && (
                  <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {files.map((f, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px', borderRadius: 6,
                        background: f.status === 'done' ? 'var(--cds-green-50)'
                          : f.status === 'error' ? 'var(--cds-red-50)'
                          : f.status === 'uploading' ? 'var(--cds-blurple-50)'
                          : 'var(--color-neutral-50)',
                        border: `1px solid ${f.status === 'done' ? 'var(--cds-green-200)' : f.status === 'error' ? 'var(--cds-red-200)' : f.status === 'uploading' ? '#C7D2FE' : 'var(--color-neutral-200)'}`,
                      }}>
                        {f.status === 'done'     && <IconCheck size={14} style={{ color: 'var(--cds-green-700)', flexShrink: 0 }} />}
                        {f.status === 'error'    && <IconAlertCircle size={14} style={{ color: 'var(--cds-red-700)', flexShrink: 0 }} />}
                        {f.status === 'uploading'&& <IconLoader2 size={14} style={{ animation: 'spin 1s linear infinite', color: 'var(--cds-blurple-700)', flexShrink: 0 }} />}
                        {f.status === 'pending'  && <IconFileText size={14} style={{ color: 'var(--color-neutral-400)', flexShrink: 0 }} />}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: 'var(--color-neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file.name}</div>
                          {f.error && <div style={{ fontSize: 11, color: 'var(--cds-red-700)' }}>{f.error}</div>}
                        </div>
                        <span style={{ fontSize: 11, color: 'var(--color-neutral-400)', flexShrink: 0 }}>{formatBytes(f.file.size)}</span>
                        {f.status === 'pending' && (
                          <button type="button" onClick={() => removeFile(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-neutral-400)', padding: 2, flexShrink: 0 }}>
                            <IconX size={13} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', gap: 8 }}
                disabled={pageState === 'uploading'}
              >
                {pageState === 'uploading'
                  ? <><IconLoader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Uploading…</>
                  : <><IconUpload size={14} /> Submit Documents</>
                }
              </button>
            </form>
          )}

          {/* Success */}
          {pageState === 'done' && (
            <div style={{ background: 'white', border: '1px solid var(--color-neutral-200)', borderRadius: 12, padding: '48px 32px', textAlign: 'center' }}>
              <div style={{
                width: 56, height: 56, borderRadius: 99, background: 'var(--cds-green-50)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
              }}>
                <IconCheck size={26} style={{ color: 'var(--cds-green-700)' }} />
              </div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 10px' }}>
                Documents submitted
              </h1>
              <p style={{ fontSize: 14, color: 'var(--color-neutral-500)', margin: '0 0 24px', lineHeight: 1.7 }}>
                Thank you, {name}. Your {files.filter(f => f.status === 'done').length} file{files.filter(f => f.status === 'done').length !== 1 ? 's' : ''} have been received and will be reviewed by the records officer.
              </p>
              {files.some(f => f.status === 'error') && (
                <div style={{ background: 'var(--cds-red-50)', borderRadius: 8, padding: '12px 16px', marginBottom: 16, textAlign: 'left' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--cds-red-700)', marginBottom: 6 }}>Some files failed to upload:</div>
                  {files.filter(f => f.status === 'error').map((f, i) => (
                    <div key={i} style={{ fontSize: 12, color: 'var(--cds-red-800)' }}>{f.file.name}: {f.error}</div>
                  ))}
                </div>
              )}
              <p style={{ fontSize: 12, color: 'var(--color-neutral-400)', margin: 0 }}>
                You may close this window.
              </p>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
